// Global Kendo Grid export context.
//
// Every Kendo Grid Excel/PDF export gets:
//   1. A file name that says where it came from:
//        Export-Clients-Locations-BP_2026-10-19_1432.xlsx
//      (page's menu label, tab if not "List", selected record when shown, local timestamp)
//   2. A title block inside the file:
//        Clients - Locations
//        Selected Client: BP
//        Exported 10/19/2026 2:32 PM
//
// The menu label comes from <span id="export-page-name"> rendered by _LayoutChild.cshtml.
// Pages without it fall back to the grid ID (EntityLocationsModelGrid -> "Entity Locations").
//
// Works by wrapping kendo.ui.Grid.prototype.saveAsExcel / saveAsPDF, so no view changes are
// needed and load order doesn't matter (the toolbar buttons call these at click time).
//
// Per-grid overrides (optional, via .HtmlAttributes on the grid):
//   data-export-title="Client Locations"  -> friendlier title / file-name segment
//   data-export-context="off"             -> keep the file name, skip the title block
// Grids that set an explicit file name (e.g. TankOverview's TankProjection.xlsx) keep it.

const DEFAULT_NAMES = { excel: "Export.xlsx", pdf: "Export.pdf" } as const;
const HEADER_CLASS = "export-context-header";

interface ExportContext {
  title: string;       // "Entity Locations"
  fileTitle: string;   // "EntityLocations"
  selection: string;   // "Selected Entity: BP" (or "")
  recordName: string;  // "BP" (or "")
  exportedAt: string;  // "10/19/2026 2:32 PM"
  stamp: string;       // "2026-10-19_1432"
}

function humanize(id: string): string {
  return id
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
}

function safeFileSegment(text: string, max = 40): string {
  return text
    .replace(/[\\/:*?"<>|#%]+/g, "")
    .trim()
    .replace(/\s+/g, "_")
    .slice(0, max);
}

// Finds the page's "<h4>Selected X: <strong>name</strong></h4>" heading in the grid's tab.
function findSelectionHeading(grid: any): JQuery | null {
  const el: JQuery = grid.element;
  const pane = el.closest('[role="tabpanel"], .k-tabstrip-content, .tab-pane');
  const scope = pane.length ? pane : el.parent();
  const h4 = scope
    .find("h4")
    .filter((_: number, h: HTMLElement) => /^\s*Selected\b/i.test(h.textContent || ""))
    .first();
  return h4.length ? h4 : null;
}

// The page's menu label ("Clients", "Q&R Assignments"), emitted by _LayoutChild.cshtml.
function pageName(): string {
  const el = document.getElementById("export-page-name");
  return ((el && el.getAttribute("data-page-name")) || "").replace(/\s+/g, " ").trim();
}

// Text of the Kendo tab whose content pane holds the grid ("Locations"), or "" if none.
function tabName(grid: any): string {
  const pane: JQuery = grid.element.closest('[role="tabpanel"], .k-tabstrip-content');
  if (!pane.length) return "";
  const paneId = pane.attr("id");
  let tab: JQuery = paneId ? $(`[aria-controls="${paneId}"]`).first() : $();
  if (!tab.length) {
    // Fallback: the tab at the same position as the content pane.
    const strip = pane.closest(".k-tabstrip");
    const idx = strip.children('.k-tabstrip-content, [role="tabpanel"]').index(pane);
    tab = idx >= 0 ? strip.find(".k-tabstrip-items .k-item").eq(idx) : $();
  }
  const label = tab.find(".k-link-text").text() || tab.text() || "";
  return label.replace(/\s+/g, " ").trim();
}

// Title parts, most specific last: ["Clients", "Locations"], ["Q&R Assignments"].
function titleParts(grid: any, gridId: string): string[] {
  const explicitTitle = (grid.element.attr("data-export-title") || "").trim();
  if (explicitTitle) return [explicitTitle];

  const parts: string[] = [];
  const page = pageName();
  const tab = tabName(grid);
  if (page) parts.push(page);
  // The List tab is just the page itself; other tabs (Locations, Contacts...) add context.
  if (tab && !/^list$/i.test(tab) && tab.toLowerCase() !== page.toLowerCase()) parts.push(tab);
  if (!parts.length) parts.push(humanize(gridId) || "Export");
  return parts;
}

function buildContext(grid: any): ExportContext {
  const el: JQuery = grid.element;
  const id = (el.attr("id") || "").replace(/(Model)?Grid$/i, "");
  const parts = titleParts(grid, id);

  const title = parts.join(" - ");
  const fileTitle = parts.map((p) => safeFileSegment(p)).filter(Boolean).join("-") || "Grid";

  const h4 = findSelectionHeading(grid);
  const selection = h4 ? (h4.text() || "").replace(/\s+/g, " ").trim() : "";
  const recordName = h4 ? (h4.find("strong").text() || "").trim() : "";

  const now = new Date();
  return {
    title,
    fileTitle,
    selection,
    recordName,
    exportedAt: kendo.toString(now, "g"),
    stamp: kendo.toString(now, "yyyy-MM-dd_HHmm"),
  };
}

function wantsHeader(grid: any): boolean {
  return (grid.element.attr("data-export-context") || "").toLowerCase() !== "off";
}

// Sets an auto file name unless the grid configured its own.
function applyFileName(grid: any, key: "excel" | "pdf", ext: string, ctx: ExportContext) {
  const opts = (grid.options[key] ||= {});
  if (opts.fileName && opts.fileName !== DEFAULT_NAMES[key] && !opts.__autoName) return;
  const record = ctx.recordName ? "-" + safeFileSegment(ctx.recordName) : "";
  opts.fileName = `Export-${ctx.fileTitle}${record}_${ctx.stamp}.${ext}`;
  opts.__autoName = true; // re-stamp on the next export instead of treating it as explicit
}

// ── Excel: insert title rows above the column headers ───────────────────────
function addExcelHeader(e: any, ctx: ExportContext) {
  const sheet = e.workbook?.sheets?.[0];
  if (!sheet || !Array.isArray(sheet.rows)) return;

  const span = Math.max((sheet.columns && sheet.columns.length) || 1, 1);
  const lines: any[] = [{ value: ctx.title, bold: true, fontSize: 14 }];
  if (ctx.selection) lines.push({ value: ctx.selection, bold: true });
  lines.push({ value: `Exported ${ctx.exportedAt}`, italic: true, color: "#666666" });

  const headerRows = lines.map((cell) => ({ cells: [{ ...cell, colSpan: span }] }));
  sheet.rows.unshift(...headerRows);
  const offset = headerRows.length;

  // Kendo's ooxml puts the autofilter on row (freezePane.rowSplit || frozenRows || 1) - 1,
  // so shift the freeze down with the inserted rows to keep the filter on the column headers.
  if (sheet.freezePane && sheet.freezePane.rowSplit) {
    sheet.freezePane.rowSplit += offset;
  } else if (sheet.frozenRows) {
    sheet.frozenRows += offset;
  } else if (sheet.filter) {
    sheet.freezePane = { ...(sheet.freezePane || {}), rowSplit: offset + 1 };
  }
}

// ── PDF: a header block that's only visible while Kendo draws the PDF ───────
// drawDOM adds .k-pdf-export to the element being drawn (a clone of the grid wrapper), so the
// block stays hidden on screen and appears at the top of every exported page.
function ensurePdfHeaderStyles() {
  if (document.getElementById(HEADER_CLASS + "-styles")) return;
  const style = document.createElement("style");
  style.id = HEADER_CLASS + "-styles";
  style.textContent = `
    .${HEADER_CLASS} { display: none; }
    .k-pdf-export .${HEADER_CLASS} { display: block; padding: 4px 6px 8px; font-family: inherit; color: #000; }
    .k-pdf-export .${HEADER_CLASS} .ech-title { font-size: 16px; font-weight: bold; }
    .k-pdf-export .${HEADER_CLASS} .ech-selection { font-size: 13px; font-weight: bold; }
    .k-pdf-export .${HEADER_CLASS} .ech-exported { font-size: 11px; font-style: italic; color: #555; }
  `;
  document.head.appendChild(style);
}

function buildPdfHeader(ctx: ExportContext): JQuery {
  const header = $(`<div class="${HEADER_CLASS}"></div>`);
  header.append($('<div class="ech-title"></div>').text(ctx.title));
  if (ctx.selection) header.append($('<div class="ech-selection"></div>').text(ctx.selection));
  header.append($('<div class="ech-exported"></div>').text(`Exported ${ctx.exportedAt}`));
  return header;
}

// ── Patch the Grid prototype ────────────────────────────────────────────────
function install() {
  const k: any = (window as any).kendo;
  const proto: any = k?.ui?.Grid?.prototype;
  if (!proto || proto.__exportContextInstalled) return;
  proto.__exportContextInstalled = true;

  const originalExcel = proto.saveAsExcel;
  if (originalExcel) {
    proto.saveAsExcel = function (this: any, ...args: any[]) {
      try {
        const ctx = buildContext(this);
        applyFileName(this, "excel", "xlsx", ctx);
        // Bound after the page's own .Events(ExcelExport) handlers, so those still see the
        // original row layout (EmployeeCheckIn / EmployeeCheckInAdmin index into sheet.rows).
        if (wantsHeader(this)) this.one("excelExport", (e: any) => addExcelHeader(e, ctx));
      } catch (err) {
        console.warn("export-context: Excel naming/header skipped", err);
      }
      return originalExcel.apply(this, args);
    };
  }

  const originalPdf = proto.saveAsPDF;
  if (originalPdf) {
    proto.saveAsPDF = function (this: any, ...args: any[]) {
      let header: JQuery | null = null;
      try {
        const ctx = buildContext(this);
        applyFileName(this, "pdf", "pdf", ctx);
        if (wantsHeader(this)) {
          ensurePdfHeaderStyles();
          header = buildPdfHeader(ctx);
          (this.wrapper || this.element).prepend(header);
        }
      } catch (err) {
        console.warn("export-context: PDF naming/header skipped", err);
      }
      const result = originalPdf.apply(this, args);
      // Kendo clones the wrapper asynchronously, so remove the block only once the export
      // settles. saveAsPDF returns nothing when a pdfExport handler cancels it.
      if (header) {
        if (result && typeof result.always === "function") result.always(() => header!.remove());
        else header.remove();
      }
      return result;
    };
  }
}

install();

export {};
